import { Injectable } from '@angular/core';
import { MessageBlockType } from '../../domain/enums/message-block-type.enum';
import { MessageRole } from '../../domain/enums/message-role.enum';
import { ConversationStatus } from '../../domain/enums/conversation-status.enum';
import { ChatConversation } from '../../domain/models/chat-conversation.model';
import { SourceType } from '../../domain/enums/source-type.enum';
import { ChatMessage } from '../../domain/models/chat-message.model';
import { MessageBlock } from '../../domain/models/message-block.model';
import { ResponseMetadata } from '../../domain/models/response-metadata.model';
import { SourceReference } from '../../domain/models/source-reference.model';
import { ChatResponseDto, ChatSourceDto } from '../../infrastructure/dto/chat-response.dto';
import {
  ConversationRestoreMessageDto,
  ConversationRestoreResponseDto,
} from '../../infrastructure/dto/conversation-restore-response.dto';
import { ConversationSummary } from '../../domain/models/conversation-summary.model';

@Injectable()
export class ChatbotMapper {
  toConversation(
    response: ConversationRestoreResponseDto | null | undefined,
    summary: ConversationSummary,
  ): ChatConversation {
    const conversationId = response?.conversation_id || summary.conversationId || summary.id || '';
    const createdAt = summary.lastActivityDate;
    const messages = Array.isArray(response?.messages) ? response.messages : [];

    return {
      id: conversationId,
      title: summary.title || summary.previewText,
      createdAt,
      updatedAt: summary.lastActivityDate,
      messages: messages
        .filter((message): message is ConversationRestoreMessageDto => !!message && typeof message === 'object')
        .map((message, index) => this.toRestoredMessage(message, conversationId, index)),
      status: ConversationStatus.ACTIVE,
    };
  }

  toAssistantMessage(
    response: ChatResponseDto,
    conversationId: string,
    messageId: string,
    blockId: string,
  ): ChatMessage {
    const metadata = this.toResponseMetadata(response.processing_metadata);
    const sources = this.toSourceReferences(response.sources);

    // hero_card and token are preserved untyped until their contracts are confirmed; they are not rendered.
    const block: MessageBlock = {
      id: blockId,
      type: MessageBlockType.MARKDOWN,
      markdown: response.answer ?? '',
      sources,
      metadata,
      data: {
        heroCard: response.hero_card ?? null,
        token: response.token ?? null,
      },
    };

    return {
      id: messageId,
      conversationId,
      role: MessageRole.ASSISTANT,
      createdAt: new Date().toISOString(),
      blocks: [block],
      metadata: { responseMetadata: metadata },
    };
  }

  toSourceReferences(sources: readonly ChatSourceDto[] | null | undefined): readonly SourceReference[] {
    if (!sources?.length) {
      return [];
    }

    return sources
      .map((source: ChatSourceDto) => this.toSourceReference(source))
      .filter((source: SourceReference | null): source is SourceReference => source !== null);
  }

  toResponseMetadata(processingMetadata: Readonly<Record<string, unknown>> | null | undefined): ResponseMetadata {
    return {
      timestamp: this.readString(processingMetadata, 'timestamp') ?? new Date().toISOString(),
      plantScope: [],
      recordsAnalyzed: this.readNumber(processingMetadata, 'records_analyzed'),
      confidence: this.readNumber(processingMetadata, 'confidence'),
    };
  }

  private toSourceReference(source: ChatSourceDto): SourceReference | null {
    const name = source?.name ?? source?.title ?? null;
    const id = source?.id ?? name;

    if (!id) {
      return null;
    }

    return {
      id: String(id),
      name: name ? String(name) : String(id),
      type: this.toSourceType(source?.type),
    };
  }

  private toRestoredMessage(
    message: ConversationRestoreMessageDto,
    conversationId: string,
    index: number,
  ): ChatMessage {
    const role = this.toMessageRole(message.sender);
    const text = typeof message.text === 'string' ? message.text : '';
    const metadata = message.metadata && typeof message.metadata === 'object' ? message.metadata : {};

    return {
      id: `${conversationId}-message-${index}`,
      conversationId,
      role,
      createdAt: this.toRestoredMessageCreatedAt(message.timestamp),
      blocks: [
        {
          id: `${conversationId}-block-${index}`,
          type: role === MessageRole.USER ? MessageBlockType.TEXT : MessageBlockType.MARKDOWN,
          text: role === MessageRole.USER ? text : undefined,
          markdown: role === MessageRole.USER ? undefined : text,
          data: Object.keys(metadata).length ? { metadata } : undefined,
        },
      ],
    };
  }

  private toRestoredMessageCreatedAt(timestamp: string | null | undefined): string {
    const rawTimestamp = typeof timestamp === 'string' ? timestamp.trim() : '';
    if (!rawTimestamp) {
      return new Date().toISOString();
    }

    const parsedDate = new Date(rawTimestamp);
    if (Number.isNaN(parsedDate.getTime())) {
      return new Date().toISOString();
    }

    return parsedDate.toISOString();
  }

  private toMessageRole(sender: string | null | undefined): MessageRole {
    switch ((sender || '').toLowerCase()) {
      case 'user':
        return MessageRole.USER;
      case 'system':
        return MessageRole.SYSTEM;
      default:
        return MessageRole.ASSISTANT;
    }
  }

  private toSourceType(rawType: string | null | undefined): SourceType {
    const normalized = (rawType ?? '').toUpperCase();
    const supportedTypes = Object.values(SourceType) as string[];

    // Backend source taxonomy is unconfirmed; unknown values fall back to DATABASE.
    return supportedTypes.includes(normalized) ? (normalized as SourceType) : SourceType.DATABASE;
  }

  private readString(source: Readonly<Record<string, unknown>> | null | undefined, key: string): string | undefined {
    const value = source?.[key];

    return typeof value === 'string' && value ? value : undefined;
  }

  private readNumber(source: Readonly<Record<string, unknown>> | null | undefined, key: string): number | undefined {
    const value = source?.[key];

    return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
  }
}
